import api from 'api';
import { localeLanguageCodes } from 'shared-locales';
import { useReduxState } from 'hooks';
import { DisplayFormattedDate, TitleWithIcon, User } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import {
  CompetenciesCard,
  CompetenciesCardLevel,
  GrantorCommentIcon
} from 'components/competencies';
import { ProfileCompetenciesEndorsements } from '.';

import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';

interface Props {
  competency: MergedCompetency;
  enableAdminLinks?: boolean;
  onHide: () => void;
  showEndorsements?: boolean;
  showLanguageVersionGrantorTitle?: boolean;
  targetUserId?: number;
  updateCompetencies?: () => Promise<void>;
  visible: boolean;
}

const ProfileCompetenciesDetails = ({
  competency,
  enableAdminLinks,
  onHide,
  showEndorsements,
  showLanguageVersionGrantorTitle,
  targetUserId,
  updateCompetencies,
  visible
}: Props) => {
  const { language, messages, theme, user: loggedInUser } = useReduxState();

  const handleDelete = async (
    competencyId: number,
    deletionReason?: string
  ) => {
    let url = `/admin/users/${targetUserId}/competencies/${competencyId}`;

    if (deletionReason) {
      const params = new URLSearchParams({
        deletionReason: deletionReason
      });
      url += `?${params.toString()}`;
    }

    await api.delete(url);
    await updateCompetencies();
    onHide();

    return true;
  };

  const handleDeleteLevel = async (
    competencyId: number,
    levelId: number,
    deletionReason?: string
  ) => {
    let url = `/admin/users/${targetUserId}/competencies/${competencyId}/${levelId}`;

    if (deletionReason) {
      const params = new URLSearchParams({
        deletionReason: deletionReason
      });
      url += `?${params.toString()}`;
    }

    await api.delete(url);
    await updateCompetencies();
    onHide();

    return true;
  };

  const CompetencyGrantor = ({
    grantor
  }: {
    grantor: MergedCompetency['grantors'][0];
  }) => {
    let grantorTitle = grantor.title;

    if (showLanguageVersionGrantorTitle && grantor.courseCompletion) {
      const preferredLanguage =
        grantor.courseCompletion?.courseLanguageVersion ||
        localeLanguageCodes[language];

      const languageVersion = grantor.courseCompletion.Course
        ?.languageVersions?.[preferredLanguage]?.isPublished
        ? grantor.courseCompletion.Course.languageVersions?.[preferredLanguage]
        : null;

      grantorTitle = languageVersion?.courseTitle || grantorTitle;
    }

    return (
      <div className="grantor">
        {grantor.type === 'user' ? (
          <User
            enableProfileLink={enableAdminLinks}
            size="small"
            subtitle={
              <span className="grantor-subtitle">
                {grantor.createdAt ? (
                  <>
                    <span className="sr-only">{messages.commonGrantedOn} </span>
                    <DisplayFormattedDate
                      date={grantor.createdAt}
                      withTime={false}
                    />
                  </>
                ) : null}
                <GrantorCommentIcon comment={grantor.comment} />
              </span>
            }
            showEmail={false}
            titleStyle={{ fontSize: '1.3rem' }}
            user={grantor.user}
          />
        ) : (
          <TitleWithIcon
            avatarProps={{
              fontSize: '1.2rem',
              padding: '6px 0',
              size: '26px',
              style: { marginRight: -2 }
            }}
            icon={
              grantor.type === 'calendar-event'
                ? ['far', 'calendar-days']
                : grantor.type === 'course'
                ? 'book'
                : grantor.type === 'path'
                ? 'tasks'
                : 'check-circle'
            }
            title={grantorTitle}
            titleLink={
              grantor.grantorIdentifier
                ? enableAdminLinks
                  ? {
                      achievement: {
                        as: `/admin/achievements/${grantor.grantorIdentifier}`,
                        href: '/admin/achievements/[achievementId]'
                      },
                      'calendar-event': {
                        as: `/calendar/${grantor.grantorIdentifier}`,
                        href: '/calendar/[calendarEventId]'
                      },
                      course: {
                        as: `/admin/courses/${grantor.grantorIdentifier}`,
                        href: '/admin/courses/[courseSlug]'
                      },
                      path: {
                        as: `/admin/paths/${grantor.grantorIdentifier}`,
                        href: '/admin/paths/[pathId]'
                      }
                    }[grantor.type]
                  : {
                      achievement: {
                        as: `/achievements/${grantor.grantorIdentifier}`,
                        href: '/achievements/[achievementId]'
                      },
                      'calendar-event': {
                        as: `/calendar/${grantor.grantorIdentifier}`,
                        href: '/calendar/[calendarEventId]'
                      },
                      course: {
                        as: `/courses/${grantor.grantorIdentifier}`,
                        href: '/courses/[...courseParams]'
                      },
                      path: {
                        as: `/paths/${grantor.grantorIdentifier}`,
                        href: '/paths/[pathId]'
                      }
                    }[grantor.type]
                : null
            }
            titleStyle={{ fontSize: '1.3rem' }}
            subtitleStyle={{ fontSize: '1.1rem', lineHeight: 1 }}
          >
            {grantor.createdAt ? (
              <>
                <span className="sr-only">{messages.commonGrantedOn} </span>
                <DisplayFormattedDate
                  date={grantor.createdAt}
                  withTime={false}
                />
              </>
            ) : null}
          </TitleWithIcon>
        )}
      </div>
    );
  };

  const loggedInUserHasEditPermissions =
    loggedInUser?.roles.includes('superadmin') ||
    (loggedInUser?.roles.includes('admin') &&
      loggedInUser?.permissions.includes('manage-users'));

  const loggedInUserIsSelfGrantor = competency.grantors?.some(
    (grantor) =>
      grantor.type === 'user' &&
      targetUserId &&
      grantor.user?.id === targetUserId &&
      targetUserId === loggedInUser?.id
  );

  const endorsements = competency.endorsements?.length
    ? competency.endorsements
    : competency.levels
    ? competency.levels.flatMap((level) => level.endorsements)
    : null;

  const firstGrantDate = competency.grantors?.length
    ? competency.grantors
        .filter((grantor) => grantor.createdAt)
        .sort(
          (a, b) =>
            new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime()
        )[0]
        ?.createdAt?.toString()
    : null;

  return (
    <ModalPortal>
      <Modal
        closeButton={true}
        onHide={onHide}
        title={messages.commonCompetence}
        visible={visible}
        width="medium"
      >
        <ModalContent style={{ padding: 0 }}>
          <div className="competency">
            <CompetenciesCard
              description={
                competency.descriptionLocales?.[language] ||
                competency.description
              }
              expirationDate={competency.expirationDate as string}
              firstGrantDate={firstGrantDate}
              grantors={
                competency.grantors?.length
                  ? competency.grantors?.map((grantor) => (
                      <CompetencyGrantor key={grantor.id} grantor={grantor} />
                    ))
                  : null
              }
              id={competency.id}
              isChecked={!competency.levels}
              name={competency.nameLocales?.[language] || competency.name}
              onDelete={
                (loggedInUserHasEditPermissions || loggedInUserIsSelfGrantor) &&
                targetUserId &&
                !competency.levels &&
                updateCompetencies
                  ? handleDelete
                  : undefined
              }
              onDeleteConfirmWarning={
                !loggedInUserHasEditPermissions &&
                loggedInUserIsSelfGrantor &&
                competency.grantors?.length > 1
                  ? messages.competenciesRemoveConfirmSelf
                  : undefined
              }
            >
              {competency.levels ? (
                <div className="levels">
                  {competency.levels.map((level) => {
                    const loggedInUserIsSelfGrantorForLevel =
                      level.grantors?.some(
                        (grantor) =>
                          grantor.type === 'user' &&
                          targetUserId &&
                          grantor.user?.id === targetUserId &&
                          targetUserId === loggedInUser?.id
                      );

                    const levelFirstGrantDate = level.grantors?.length
                      ? level.grantors
                          .filter((grantor) => grantor.createdAt)
                          .sort(
                            (a, b) =>
                              new Date(a.createdAt!).getTime() -
                              new Date(b.createdAt!).getTime()
                          )[0]
                          ?.createdAt?.toString()
                      : null;

                    return (
                      <CompetenciesCardLevel
                        key={level.id}
                        competencyId={competency.id}
                        description={
                          level.descriptionLocales?.[language] ||
                          level.description
                        }
                        firstGrantDate={levelFirstGrantDate}
                        grantedByHigherLevel={
                          competency.highestLevel?.level > level.level
                            ? true
                            : undefined
                        }
                        grantors={
                          level.grantors?.length
                            ? level.grantors.map((grantor) => (
                                <CompetencyGrantor
                                  key={grantor.id}
                                  grantor={grantor}
                                />
                              ))
                            : null
                        }
                        id={level.id}
                        isChecked={
                          Boolean(level.grantors?.length) ||
                          competency.highestLevel?.level > level.level
                        }
                        name={level.nameLocales?.[language] || level.name}
                        onDelete={
                          (loggedInUserHasEditPermissions ||
                            loggedInUserIsSelfGrantorForLevel) &&
                          targetUserId &&
                          level.grantors?.length &&
                          updateCompetencies
                            ? handleDeleteLevel
                            : undefined
                        }
                        onDeleteConfirmWarning={
                          !loggedInUserHasEditPermissions &&
                          loggedInUserIsSelfGrantorForLevel &&
                          level.grantors?.length > 1
                            ? messages.competenciesRemoveConfirmSelf
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              ) : null}
            </CompetenciesCard>

            {showEndorsements && endorsements?.length ? (
              <ProfileCompetenciesEndorsements
                competency={competency}
                style={{ marginTop: 15 }}
                updateCompetencies={updateCompetencies}
              />
            ) : null}

            <style jsx>{`
              .competency {
                background-color: ${theme.lightGreyLighter};
                padding: 15px;
              }
            `}</style>
          </div>
        </ModalContent>
      </Modal>
    </ModalPortal>
  );
};

export default ProfileCompetenciesDetails;
